import { useTranslations } from 'next-intl';
import { createTask, createTaskByCraft, createTaskByTool } from '@/app/apis';
import { useToast } from '@/app/components/ui/use-toast';
import { SCENE } from '@/app/constants';
import { useStore } from '@/app/providers/store';
import type { TaskInfo, TaskParams } from '@/app/types';
import { getDefaultReportParams, reportEvent } from '@/app/utils';
import { getFetchErrorMsg } from '@/app/utils/client';
import { usePathname } from '@/i18n/routing';
import { useCredits } from './use-credits';
import { useFetch } from './use-fetch';
import { usePWACount } from './use-pwa-count';
import { type ReportEventParams, useTaskSubmitError } from './use-task-submit-error';

interface TaskSubmitOptions {
  taskType: TaskParams['type'];
  isFree: boolean;
  templateScene?: string;
  templateId?: string;
  /**
   * 提交成功回调
   * @returns 返回 'suppressDefaultToast' 时不显示默认 toast，否则显示默认 toast
   */
  onSubmitSuccess?: (task: TaskInfo) => 'suppressDefaultToast' | undefined;
  onCloseDialog?: () => void;
  // 用于埋点上报
  getReportParams?: (isNewbieTask?: boolean) => ReportEventParams;
  /**
   * 当检测到 ImagePromoCreationShortage 错误时的回调，用于限制数量为1
   */
  onImagePromoCreationShortage?: () => void;
}

const TOOL_TASK_TYPES = new Set<string>([
  'video2extend',
  'video2extail',
  'video_edit',
  'image_edit',
  'upscale_image',
  'one_click',
  'music_video',
  'lip_sync',
  'remake',
  'face_swap',
]);

/**
 * 处理通用的表单提交逻辑，包括提交失败后出错误弹窗，提交成功后处理积分更新、上报事件、添加新任务、统计生成次数达到一定量后弹订阅弹窗等逻辑
 */
export const useTaskSubmit = ({
  taskType,
  isFree,
  templateScene,
  onSubmitSuccess,
  templateId,
  getReportParams,
  onCloseDialog,
  onImagePromoCreationShortage,
}: TaskSubmitOptions) => {
  const { updateCredits, updateCreditsAsync } = useCredits();
  const t = useTranslations();
  const { toast } = useToast();
  const pathname = usePathname();

  const addNewTask = useStore((state) => state.addNewTask);

  // 错误弹窗
  const { handleError, handleDialogOpenChange } = useTaskSubmitError({
    isFree,
    templateScene: templateScene ?? '',
    taskType,
    onCloseDialog,
    onImagePromoCreationShortage,
  });

  const { incrementPWAInstallCount } = usePWACount();
  // 接口：提交任务
  const { run: submitTask, loading: submitting } = useFetch(
    (body: TaskParams, _costCredits?: number) => {
      // 创建模板任务和模板创建超分调用tasksCreateByCraft
      const shouldUseCraft = taskType === SCENE || templateScene;
      const shouldUseTool = TOOL_TASK_TYPES.has(taskType);

      if (shouldUseCraft) {
        return createTaskByCraft({
          body: {
            [SCENE]: templateScene,
            id: templateId,
            task: body,
            method: 'template_based',
          },
        });
      }

      if (shouldUseTool) {
        return createTaskByTool({
          body: body as Parameters<typeof createTaskByTool>[0]['body'],
        });
      }

      return createTask({
        body,
      });
    },
    {
      manual: true,
      onSuccess: ({ data, error }, [body, costCredits]) => {
        const isNewbieTask = !!data?.extra_params?.matched_nexus_set;
        const reportEventParams = getReportParams
          ? getReportParams(isNewbieTask)
          : getDefaultReportParams({
              value: body,
              isInspireMe: false,
              templateScene,
              isNewbieTask: isNewbieTask,
            });

        if (error) {
          handleError(error, reportEventParams);
          return;
        }

        if (data) {
          // 处理积分更新
          if (taskType === 'upscale') {
            // upscale 需要等积分更新完再关闭弹窗
            updateCreditsAsync().then(() => handleDialogOpenChange(false));
          } else {
            updateCredits();
            handleDialogOpenChange(false);
          }

          const reportSuccessEventParams = {
            ...reportEventParams,
            is_success: true,
            task_id: data.id,
          };

          // 上报点击提交按钮事件
          reportEvent(
            'create_video_click',
            taskType === 'upscale'
              ? {
                  ...reportSuccessEventParams,
                  interaction_position: pathname,
                }
              : reportSuccessEventParams,
          );

          if (taskType === 'video2extend' || taskType === 'video2extail') {
            reportEvent('extend_submit_click', {
              ...reportSuccessEventParams,
              extend_model_type: body.settings?.transition === 'extendspeed' ? '3.1_turbo' : '3.1_pro',
              with_last_frame: taskType === 'video2extail',
              interaction_position: pathname,
              consume_points: costCredits ?? 0,
              ratio: reportSuccessEventParams.resolution ?? '',
            });
          }

          // 这个 toast 可以被 onSubmitSuccess 的返回值控制
          // 返回 'suppressDefaultToast' 时不显示默认 toast
          const result = onSubmitSuccess?.(data);

          if (result !== 'suppressDefaultToast') {
            toast({
              message: t('popup.toast.taskSubmitSuccess'),
              type: 'success',
            });
          }
          // 在模版页面通过快速编辑提交的参考生图任务，不添加到任务历史列表
          const isTemplatesPage = pathname.match(/\/templates\/\w+/);
          const shouldSkipAddTask = isTemplatesPage && taskType === 'reference2image';
          if (!shouldSkipAddTask) {
            addNewTask(data);
          }

          // 上报任务排队中曝光事件（时机：排队中元素曝光时），如果虚拟列表没渲染 data-index="0" 的元素，则表示当前卡片没有曝光，不上报
          if (document.getElementById('taskHistoryContainer')?.querySelector('div[data-index="0"]')) {
            reportEvent('create_video_waiting', reportSuccessEventParams);
          }

          incrementPWAInstallCount();
        }
      },
      onError: (error) => {
        const msg = getFetchErrorMsg(error, t);
        toast({
          type: 'error',
          message: msg,
        });
      },
    },
  );

  return {
    submitTask,
    submitting,
  };
};
